import React, { useCallback } from "react";
import { FaTrashCan } from "react-icons/fa6";
import type { FormikProps } from "formik";

// global functions
import { isEmptyObject, rupeeConverter } from "../../global/function";

// components
import Switch from "../form-inputs/switch.component";

// queries
import type { Preference } from "../../queries/company.queries";
import type { BillTotalsValues } from "../../hooks/useBillTotals";

type InvoiceItemsFooterFormValues = BillTotalsValues & { sale_info: string };

interface BilledItemErrors {
	item_name?: string;
	item_qty?: string;
	item_price?: string;
	total_price?: string;
}

interface InvoiceItemsFooterProps<T extends InvoiceItemsFooterFormValues> {
	formik: FormikProps<T>;
	onDiscountServiceChange: (fieldName: string, fieldValue: number) => void;
	onBilledItemsChange: (
		fieldName: string,
		index: number,
		itemName: string,
		newValue: string | number | boolean,
	) => void;
	onServiceChargeToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
	isServiceChargeEnabled: boolean;
	onFieldValueChange: (fieldName: string, fieldValue: unknown) => void;
	preferenceDetails?: Partial<Preference>;
}

export default function InvoiceItemsFooter<
	T extends InvoiceItemsFooterFormValues,
>({
	formik,
	onDiscountServiceChange,
	onBilledItemsChange,
	onServiceChargeToggle,
	isServiceChargeEnabled,
	onFieldValueChange,
	preferenceDetails,
}: InvoiceItemsFooterProps<T>) {
	// VAT and ESF are either/or (enforced in company preferences) — whichever is
	// active, billed_items[i].vatable means "subject to that charge" and drives
	// the same non-taxable/chargeable split either way.
	const chargeMode = preferenceDetails?.vat
		? "vat"
		: (preferenceDetails?.esf ?? 0) > 0
			? "esf"
			: null;

	// Formik's FormikErrors<T> types an array field's errors as
	// `string | FormikErrors<Item>[]` — narrowing that per-access is more
	// friction than it's worth for a form this shape has never actually sent
	// a top-level string error for.
	const billedItemErrors = formik.errors.billed_items as
		| BilledItemErrors[]
		| undefined;

	const handleAddItem = useCallback(() => {
		if (!isEmptyObject(preferenceDetails)) {
			formik.setFieldValue("billed_items", [
				...formik.values.billed_items,
				{
					item_name: "",
					hs_code: "",
					item_price: 0,
					item_qty: 0,
					total_price: 0,
					vatable:
						!!preferenceDetails?.vat ||
						(preferenceDetails?.esf ?? 0) > 0,
				},
			]);
		}
	}, [formik, preferenceDetails]);

	const handleRemoveItem = (index: number) => {
		const updatedItems = [...formik.values.billed_items];
		updatedItems.splice(index, 1);
		formik.setFieldValue("billed_items", updatedItems);
	};

	// VAT and ESF are either/or, but structurally identical: a two-column
	// non-taxable/chargeable split flowing through discount + service charge,
	// then a single charge row (VAT @ 13% or ESF @ rate%) applied to the
	// chargeable column only. `mode` is "vat" | "esf".
	function _renderChargeSection(mode: "vat" | "esf") {
		const isVat = mode === "vat";
		const chargeLabel = isVat ? "VAT" : "ESF";
		const chargeRate = isVat ? 13 : preferenceDetails?.esf;
		const chargeBaseValue = isVat
			? formik.values?.taxable_sales_vat
			: formik.values?.amount_for_esf;
		const chargeAmountValue = isVat
			? formik.values?.vat
			: formik.values?.esf;
		const chargeColumnHeader = isVat
			? "Taxable Amount"
			: "ESF Applicable Amount";

		return (
			<div className="bill-total-container">
				<div className="bill-total-row">
					<div className="row-item"></div>
					<div className="row-item align_right item-header">
						Non-taxable Amount
					</div>
					<div className="row-item align_right item-header">
						{chargeColumnHeader}
					</div>
				</div>
				<div className="bill-total-row">
					<div className="row-item">Subtotal</div>
					<div className="row-item align_right">
						Rs.{" "}
						{rupeeConverter(formik.values?.non_taxable_total_sales)}
					</div>
					<div className="row-item align_right">
						Rs. {rupeeConverter(formik.values?.taxable_total_sales)}
					</div>
				</div>

				{preferenceDetails?.discount && (
					<div className="bill-total-row">
						<div className="row-item">Discount</div>
						<div className="row-item">
							<input
								name="non_taxable_discount"
								className="input-box-wrapper"
								style={{ textAlign: "right" }}
								type="number"
								placeholder="Rs. 0.00"
								onChange={(e) =>
									onDiscountServiceChange(
										"non_taxable_discount",
										Number(e.target.value),
									)
								}
								onBlur={formik.handleBlur}
								value={
									formik.values?.non_taxable_discount || ""
								}
							/>
						</div>
						<div className="row-item">
							<input
								name="taxable_discount"
								className="input-box-wrapper"
								style={{ textAlign: "right" }}
								type="number"
								placeholder="Rs. 0.00"
								onChange={(e) =>
									onDiscountServiceChange(
										"taxable_discount",
										Number(e.target.value),
									)
								}
								onBlur={formik.handleBlur}
								value={formik.values?.taxable_discount || ""}
							/>
						</div>
					</div>
				)}

				{(preferenceDetails?.service_charge ?? 0) > 0 && (
					<div className="bill-total-row">
						<div className="row-item">
							<span>Service Charge</span>
							<Switch
								name="isService"
								isToggled={isServiceChargeEnabled}
								onToggle={onServiceChargeToggle}
							/>
						</div>
						<div className="row-item">
							<input
								name="non_taxable_service_charge"
								className="input-box-wrapper"
								style={{ textAlign: "right" }}
								type="text"
								placeholder="Rs. 0.00"
								onChange={(e) =>
									onDiscountServiceChange(
										"non_taxable_service_charge",
										Number(e.target.value),
									)
								}
								onBlur={formik.handleBlur}
								value={
									formik.values?.non_taxable_service_charge ||
									""
								}
								disabled={!isServiceChargeEnabled}
							/>
						</div>
						<div className="row-item">
							<input
								name="taxable_service_charge"
								className="input-box-wrapper"
								style={{ textAlign: "right" }}
								type="text"
								placeholder="Rs. 0.00"
								onChange={(e) =>
									onDiscountServiceChange(
										"taxable_service_charge",
										Number(e.target.value),
									)
								}
								onBlur={formik.handleBlur}
								value={
									formik.values?.taxable_service_charge || ""
								}
								disabled={!isServiceChargeEnabled}
							/>
						</div>
					</div>
				)}
				<div className="bill-total-row">
					<div className="row-item">
						{isVat ? "Gross Total" : "Amount for ESF"}
					</div>
					<div className="row-item align_right">
						Rs.{" "}
						{formik.values?.non_taxable_amt
							? formik.values?.non_taxable_amt?.toFixed(2)
							: "0.0"}
					</div>
					<div className="row-item align_right">
						Rs.{" "}
						{chargeBaseValue ? chargeBaseValue.toFixed(2) : "0.0"}
					</div>
				</div>

				<div className="bill-total-row">
					<div className="row-item">
						<span>
							{chargeLabel} @ {chargeRate}%
						</span>
					</div>
					<div className="row-item align_right">-</div>

					<div className="row-item align_right">
						Rs. {Number(chargeAmountValue || 0).toFixed(2)}
					</div>
				</div>

				<div className="bill-total-row highlighted">
					<div className="row-item">Total</div>
					<div className="row-item align_right">
						{/* // totalSales */}
						Rs. {rupeeConverter(formik.values?.total_sales)}
					</div>
				</div>
				<div className="total-error-container">
					{formik.errors?.taxable_discount as string | undefined}
					{formik.errors?.non_taxable_discount as string | undefined}
				</div>
			</div>
		);
	}

	// Neither VAT nor ESF enabled — a single running total, no chargeable split.
	function _renderNoChargeSection() {
		return (
			<div className="bill-total-container">
				<div className="bill-total-row">
					<div className="row-item">Subtotal</div>
					<div className="row-item align_right">
						Rs. {rupeeConverter(formik.values?.sub_total)}
					</div>
				</div>

				{preferenceDetails?.discount && (
					<div className="bill-total-row">
						<div className="row-item">Discount</div>
						<div className="row-item">
							<input
								name="non_taxable_discount"
								className="input-box-wrapper"
								style={{ textAlign: "right" }}
								type="text"
								placeholder="Rs. 0.00"
								onChange={(e) =>
									onDiscountServiceChange(
										"non_taxable_discount",
										Number(e.target.value),
									)
								}
								onBlur={formik.handleBlur}
								value={
									formik.values?.non_taxable_discount || ""
								}
							/>
						</div>
					</div>
				)}

				{(preferenceDetails?.service_charge ?? 0) > 0 && (
					<div className="bill-total-row">
						<div className="row-item">
							<span>Service Charge</span>
							<Switch
								name="isService"
								isToggled={isServiceChargeEnabled}
								onToggle={onServiceChargeToggle}
							/>
						</div>

						<div className="row-item">
							<input
								name="non_taxable_service_charge"
								className="input-box-wrapper"
								style={{ textAlign: "right" }}
								type="text"
								placeholder="Rs. 0.00"
								onChange={(e) =>
									onDiscountServiceChange(
										"non_taxable_service_charge",
										Number(e.target.value),
									)
								}
								onBlur={formik.handleBlur}
								value={
									formik.values?.non_taxable_service_charge ||
									""
								}
								disabled={!isServiceChargeEnabled}
							/>
						</div>
					</div>
				)}
				<div className="bill-total-row">
					<div className="row-item">Non-Taxable Amount</div>
					<div className="row-item align_right">
						Rs.{" "}
						{formik.values?.non_taxable_amt
							? formik.values?.non_taxable_amt?.toFixed(2)
							: "0.0"}
					</div>
				</div>

				<div className="bill-total-row highlighted">
					<div className="row-item">Total</div>
					<div className="row-item align_right">
						{/* totalSales */}
						Rs. {rupeeConverter(formik.values?.total_sales)}
					</div>
				</div>
				<div className="total-error-container">
					{formik.errors?.taxable_discount as string | undefined}
					{formik.errors?.non_taxable_discount as string | undefined}
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="bill-items-container">
				<div className="label-title">Item Descriptions</div>
				<div
					className={`item-category-container ${
						chargeMode === "vat"
							? "vat-col"
							: chargeMode === "esf"
								? "charge-col"
								: ""
					}`}
				>
					<div className="category-text">#</div>
					{chargeMode === "vat" && (
						<div className="category-text">HS Code</div>
					)}
					<div className="category-text">Items</div>
					<div className="category-text">Qty.</div>
					<div className="category-text">Price</div>
					<div className="category-text" style={{ minWidth: "64px" }}>
						Amount
					</div>
					{chargeMode && (
						<div className="category-text">
							{chargeMode === "vat" ? "VAT" : "ESF"}
						</div>
					)}
					<div className="category-text"></div>
				</div>
				{formik.values?.billed_items &&
					formik.values?.billed_items.length > 0 &&
					formik.values?.billed_items.map((item, index) => {
						return (
							<div
								className={`item-details-container  ${
									chargeMode === "vat"
										? "vat-col"
										: chargeMode === "esf"
											? "charge-col"
											: ""
								}`}
								key={index}
							>
								<div className="input-wrapper">
									<input
										name={`billed_items[${index}].count`}
										value={index + 1}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										className="input-box-wrapper"
										type="text"
										disabled={true}
									/>
								</div>
								{chargeMode === "vat" && (
									<div className="input-wrapper">
										<input
											name={`billed_items[${index}].hs_code`}
											value={
												formik.values?.billed_items[
													index
												]?.hs_code || ""
											}
											onChange={(e) =>
												onBilledItemsChange(
													`billed_items[${index}].hs_code`,
													index,
													"hs_code",
													e.target.value,
												)
											}
											onBlur={formik.handleBlur}
											className="input-box-wrapper"
											type="text"
										/>
									</div>
								)}
								<div className="input-wrapper">
									<input
										name={`billed_items[${index}].item_name`}
										value={
											formik.values?.billed_items[index]
												?.item_name || ""
										}
										onChange={(e) =>
											onBilledItemsChange(
												`billed_items[${index}].item_name`,
												index,
												"item_name",
												e.target.value,
											)
										}
										onBlur={formik.handleBlur}
										className="input-box-wrapper"
										type="text"
										placeholder="Items..."
									/>
									{formik.submitCount > 0 && (
										<div className="validation-error">
											{
												billedItemErrors?.[index]
													?.item_name
											}
										</div>
									)}
								</div>
								<div className="input-wrapper">
									<input
										name={`billed_items[${index}].item_qty`}
										value={
											formik.values?.billed_items[index]
												?.item_qty || ""
										}
										onChange={(e) => {
											onBilledItemsChange(
												`billed_items[${index}].item_qty`,
												index,
												"item_qty",
												Number(e.target.value),
											);
										}}
										onBlur={formik.handleBlur}
										className="input-box-wrapper"
										type="number"
										placeholder="0"
									/>
									{formik.submitCount > 0 && (
										<div className="validation-error">
											{
												billedItemErrors?.[index]
													?.item_qty
											}
										</div>
									)}
								</div>
								<div className="input-wrapper">
									<input
										name={`billed_items[${index}].item_price`}
										value={
											formik.values?.billed_items[index]
												?.item_price || ""
										}
										onChange={(e) => {
											onBilledItemsChange(
												`billed_items[${index}].item_price`,
												index,
												"item_price",
												Number(e.target.value),
											);
										}}
										onBlur={formik.handleBlur}
										className="input-box-wrapper"
										type="number"
										placeholder="0.00"
									/>
									{formik.submitCount > 0 && (
										<div className="validation-error">
											{
												billedItemErrors?.[index]
													?.item_price
											}
										</div>
									)}
								</div>
								<div
									className="input-wrapper"
									style={{
										minWidth: "64px",
									}}
								>
									<input
										name={`billed_items[${index}].total_price`}
										value={
											formik.values?.billed_items[index]
												.total_price
										}
										className="input-box-wrapper"
										type="number"
										placeholder="0.00"
										style={{
											textAlign: "right",
										}}
										disabled={true}
									/>
									{formik.submitCount > 0 && (
										<div className="validation-error">
											{
												billedItemErrors?.[index]
													?.total_price
											}
										</div>
									)}
								</div>
								{chargeMode && (
									<label
										htmlFor="vatable"
										className="input-action"
									>
										<input
											type="checkbox"
											id="vatable"
											name={`billed_items[${index}].vatable`}
											onChange={(e) => {
												onBilledItemsChange(
													`billed_items[${index}].vatable`,
													index,
													"vatable",
													e.target.checked,
												);
											}}
											checked={
												!!formik.values?.billed_items[
													index
												]?.vatable
											}
											onBlur={formik.handleBlur}
										/>
									</label>
								)}
								<div
									className="input-action"
									onClick={() => handleRemoveItem(index)}
								>
									<FaTrashCan />
								</div>
							</div>
						);
					})}
				<div className="row-btns">
					<div className="itemfield-btn" onClick={handleAddItem}>
						+ New invoice line
					</div>
				</div>
			</div>

			<div className="bill-footer-container">
				<div className="footer-description-container">
					<textarea
						name="sale_info"
						className="textarea-wrapper"
						rows={10}
						// cols={80}
						placeholder="Write a description to the recipent..."
						onChange={(e) =>
							onFieldValueChange("sale_info", e.target.value)
						}
						onBlur={formik.handleBlur}
						value={formik.values?.sale_info || ""}
					/>
				</div>
				{chargeMode
					? _renderChargeSection(chargeMode)
					: _renderNoChargeSection()}
			</div>
		</>
	);
}
